import type { ReactNode } from "react";

import { useNow } from "@/hooks/useNow";
import {
  formatDate,
  formatDateTime,
  formatDay,
  formatRelative,
  formatTime,
  isDateOnly,
  toDate,
  type DateInput,
} from "@/lib/format";
import { cn } from "@/lib/utils";

// One accessible time element for every date shown in the app:
// <time dateTime="2026-09-24T09:00:00.000Z" title="Thu 24 Sep 2026, 10:00">
// with tabular figures, never wrapping mid-value. The tooltip is always
// the full local date-time (never raw ISO). Relative times re-render on a
// shared clock, so "just now" does not freeze on pages that don't poll.
//
// Render it OUTSIDE `.uppercase` / mono-caps wrappers: Earth capitalises
// every word inside those ("3 Min Ago").

export type TimeMode = "relative" | "datetime" | "date" | "time" | "day";

export interface TimeProps {
  value: DateInput;
  /** Default "relative". */
  mode?: TimeMode;
  /** Shown when the value is missing or unparseable. Default: nothing. */
  fallback?: ReactNode;
  /** Lower-case "Today"/"Yesterday" for use mid-sentence. */
  inline?: boolean;
  /** Fixed "now" (tests, or a parent that already ticks). */
  now?: Date;
  /** Override the tooltip. */
  title?: string;
  className?: string;
}

export function Time({ value, mode = "relative", fallback = null, inline, now, title, className }: TimeProps) {
  // Relative labels tick every 30 s; "Today …" labels every 5 min (so
  // they turn into "Yesterday …" after midnight); plain dates never.
  const interval = now ? 0 : mode === "relative" ? 30_000 : mode === "datetime" || mode === "day" ? 300_000 : 0;
  const clock = useNow(interval);
  const at = toDate(value);
  if (!at) return <>{fallback}</>;
  const ref = now ?? (interval ? clock : new Date());
  const dateOnly = isDateOnly(value);
  let text: string;
  switch (mode) {
    case "time":
      // A date-only value has no time of day; show the day, not "00:00".
      text = dateOnly ? formatDay(at, ref, { inline }) : formatTime(at);
      break;
    case "date":
      text = formatDate(at, {}, ref);
      break;
    case "day":
      text = formatDay(at, ref, { inline });
      break;
    case "datetime":
      text = dateOnly ? formatDay(at, ref, { inline }) : formatDateTime(at, { inline }, ref);
      break;
    default:
      text = dateOnly ? formatDay(at, ref, { inline }) : formatRelative(at, ref, { inline });
  }
  const full = dateOnly
    ? formatDate(at, { year: "always" })
    : formatDateTime(at, { relativeDay: false, year: "always" });
  return (
    <time
      dateTime={dateOnly ? String(value).trim() : at.toISOString()}
      title={title ?? full}
      className={cn("whitespace-nowrap tabular-nums", className)}
    >
      {text}
    </time>
  );
}
